import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuthStore } from '../store/authStore';
import { supabase } from '../lib/supabase';

export default function ResetPasswordPage() {
    const navigate = useNavigate();
    const { updatePassword } = useAuthStore();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [isValidSession, setIsValidSession] = useState<boolean | null>(null);

    useEffect(() => {
        // Check if we have a valid recovery session
        const checkSession = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            
            // Check URL for recovery token (Supabase adds this)
            const hashParams = new URLSearchParams(window.location.hash.substring(1));
            const accessToken = hashParams.get('access_token');
            const type = hashParams.get('type');
            
            if (type === 'recovery' && accessToken) {
                // Set the session with the recovery token
                const { error } = await supabase.auth.setSession({
                    access_token: accessToken,
                    refresh_token: hashParams.get('refresh_token') || '',
                });
                
                if (error) {
                    setIsValidSession(false);
                    setError('重置链接无效或已过期');
                } else {
                    setIsValidSession(true);
                }
            } else if (session) {
                // User is already logged in and wants to change password
                setIsValidSession(true);
            } else {
                setIsValidSession(false);
                setError('无效的重置链接，请重新申请');
            }
        };

        checkSession();
    }, []);

    const validatePassword = (password: string) => {
        return password.length >= 6;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!validatePassword(password)) {
            setError('密码至少需要 6 个字符');
            return;
        }

        if (password !== confirmPassword) {
            setError('两次输入的密码不一致');
            return;
        }

        setIsLoading(true);

        try {
            await updatePassword(password);
            setSuccess(true);
            // Redirect to home after 2 seconds
            setTimeout(() => {
                navigate('/', { replace: true });
            }, 2000);
        } catch (err: unknown) {
            let message = '密码重置失败，请稍后重试';
            if (err instanceof Error) {
                if (err.message.includes('Password should be at least')) {
                    message = '密码至少需要 6 个字符';
                } else if (err.message.includes('same password')) {
                    message = '新密码不能与旧密码相同';
                } else {
                    message = err.message;
                }
            }
            setError(message);
        } finally {
            setIsLoading(false);
        }
    };

    // Loading state while checking session
    if (isValidSession === null) {
        return (
            <div className="min-h-[70vh] flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
                    <p className="text-gray-400">验证中...</p>
                </div>
            </div>
        );
    }

    // Invalid session state
    if (!isValidSession) {
        return (
            <div className="min-h-[70vh] flex items-center justify-center py-12">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="glass-card p-8 w-full max-w-md text-center"
                >
                    <div className="text-5xl mb-4">😕</div>
                    <h2 className="text-xl font-bold mb-2">链接无效</h2>
                    <p className="text-gray-400 mb-6">{error || '重置链接无效或已过期'}</p>
                    <button
                        onClick={() => navigate('/login')}
                        className="btn-primary"
                    >
                        返回登录
                    </button>
                </motion.div>
            </div>
        );
    }

    // Success state
    if (success) {
        return (
            <div className="min-h-[70vh] flex items-center justify-center py-12">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="glass-card p-8 w-full max-w-md text-center"
                >
                    <div className="text-5xl mb-4">✅</div>
                    <h2 className="text-xl font-bold mb-2">密码已重置</h2>
                    <p className="text-gray-400 mb-6">正在跳转到首页...</p>
                    <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto" />
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-[70vh] flex items-center justify-center py-12">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="glass-card p-8 w-full max-w-md"
            >
                <div className="text-center mb-8">
                    <img 
                        src="/sudoku.svg" 
                        alt="疯狂的数独" 
                        className="w-20 h-20 mx-auto mb-4"
                    />
                    <h1 className="text-2xl font-bold mb-2">设置新密码</h1>
                    <p className="text-gray-400">请输入您的新密码</p>
                </div>

                {error && (
                    <div className="mb-6 p-4 rounded-lg bg-red-500/20 border border-red-500/30 text-red-400 text-sm">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                            新密码
                        </label>
                        <input
                            type="password"
                            id="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="至少 6 个字符"
                            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                            disabled={isLoading}
                            required
                            minLength={6}
                        />
                    </div>

                    <div>
                        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                            确认新密码
                        </label>
                        <input
                            type="password"
                            id="confirmPassword"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="再次输入新密码"
                            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                            disabled={isLoading}
                            required
                            minLength={6}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full btn-primary flex items-center justify-center gap-2 py-3"
                    >
                        {isLoading ? (
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                            <span>确认重置</span>
                        )}
                    </button>
                </form>

                <div className="mt-6 text-center">
                    <button
                        onClick={() => navigate('/login')}
                        className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                    >
                        返回登录
                    </button>
                </div>
            </motion.div>
        </div>
    );
}
